'use client'

import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { THEME_STORAGE_KEY } from './constants'

/** What the user picked. `system` follows the OS and keeps following it. */
export type ThemePref = 'light' | 'dark' | 'system'
/** What is actually on screen, `system` already resolved. */
export type Theme = 'light' | 'dark'

interface ThemeContextValue {
  /** The stored preference, which the Appearance controls bind to. */
  pref: ThemePref
  /** The resolved theme, for the rare component that must branch on it. */
  theme: Theme
  setPref: (pref: ThemePref) => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

export function useTheme(): ThemeContextValue {
  const v = useContext(ThemeContext)
  if (!v) throw new Error('useTheme must be used within <ThemeProvider>')
  return v
}

function systemTheme(): Theme {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function resolve(pref: ThemePref): Theme {
  return pref === 'system' ? systemTheme() : pref
}

function readPref(): ThemePref {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY)
    return stored === 'light' || stored === 'dark' ? stored : 'system'
  } catch {
    return 'system'
  }
}

/**
 * The theme's one source of truth. Adapted from taw-ads-commander.
 *
 * The class goes on <html>, not a wrapper, because portalled surfaces (dialogs,
 * popovers, tooltips) render at the end of <body> and would otherwise keep the
 * old theme. `system` is a live subscription: an OS that flips at sunset takes
 * the app with it. First paint is handled by THEME_SCRIPT in the root layout;
 * this provider's effects run a frame too late to prevent a flash.
 */
export function ThemeProvider({ children }: { children: React.ReactNode }) {
  // Starts at the server default and is corrected after hydration. Reading
  // storage during render would differ from the server output.
  const [pref, setPrefState] = useState<ThemePref>('system')
  const [theme, setTheme] = useState<Theme>('light')

  useEffect(() => {
    const next = readPref()
    setPrefState(next)
    setTheme(resolve(next))
  }, [])

  useEffect(() => {
    if (pref !== 'system') return
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = () => setTheme(systemTheme())
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [pref])

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark')
    document.documentElement.style.colorScheme = theme
  }, [theme])

  const setPref = useCallback((next: ThemePref) => {
    setPrefState(next)
    setTheme(resolve(next))
    try {
      if (next === 'system') localStorage.removeItem(THEME_STORAGE_KEY)
      else localStorage.setItem(THEME_STORAGE_KEY, next)
    } catch {
      // Private modes can refuse storage; the choice still holds for this tab.
    }
  }, [])

  return <ThemeContext.Provider value={{ pref, theme, setPref }}>{children}</ThemeContext.Provider>
}
